// api.js - Environment-aware API configuration using .env
const API_BASE = process.env.REACT_APP_API_BASE || 'http://80.225.197.31:8001';

// Central authenticated fetch function.
// Always uses localStorage — works consistently across browser, mobile browser,
// and Android WebView.
// Automatically handles 401 (token expired or superseded by login on another device)
// by clearing all stored credentials and redirecting to login.
const authFetch = (url, options = {}) => {
  const token = localStorage.getItem('token');
  return fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      ...options.headers,
    }
  }).then(response => {
    if (response.status === 401) {
      // Token is invalid or has been superseded by a login on another device.
      // Clear all stored credentials and send student to login page.
      localStorage.removeItem('userData');
      localStorage.removeItem('student');
      localStorage.removeItem('token');
      localStorage.removeItem('currentFeatureAccess');

      // If running inside the Android app, clear SharedPreferences too
      try {
        if (window.Android && typeof window.Android.onLogout === 'function') {
          window.Android.onLogout();
        }
      } catch (e) {}

      window.location.href = '/login';
    }
    return response;
  });
};


class ApiService {
  // NEW: Ask doubt using proper LLM resolution
  async askDoubt(question, sessionId = null, studentId, subjectId = null, chapterId = null, topicId = null, language = 'en') {
    try {
      const response = await authFetch(`${API_BASE}/doubt-resolution/ask?student_id=${studentId}`, {
        method: 'POST',
        
        body: JSON.stringify({
          question: question,
          session_id: sessionId,
          student_id: studentId,
          subject_id: subjectId,
          chapter_id: chapterId,
          topic_id: topicId,
          language: language
        })
      });
      
      if (!response.ok) {
        throw new Error('Doubt resolution failed');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Doubt resolution error:', error);
      throw error;
    }
  }

  // NEW: Mark doubt as complete
  async completeDoubtSession(sessionId, studentId) {
    try {
      const response = await authFetch(`${API_BASE}/doubt-resolution/complete?student_id=${studentId}`, {
        method: 'POST',
        
        body: JSON.stringify({
          session_id: sessionId,
          student_id: studentId
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to complete doubt session');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Complete doubt error:', error);
      throw error;
    }
  }

  // Keep existing methods...
  async getSubjects(studentId) {
  try {
    const response = await authFetch(`${API_BASE}/subjects/?student_id=${studentId}`);
    if (!response.ok) throw new Error('Failed to fetch subjects');
    return await response.json();
  } catch (error) {
    console.error('Subjects fetch error:', error);
    throw error;
  }
}

  async getChapters(subjectId, studentId) {
    try {
      const response = await authFetch(`${API_BASE}/chapters/?subject_id=${subjectId}&student_id=${studentId}`);
      if (!response.ok) throw new Error('Failed to fetch chapters');
      return await response.json();
    } catch (error) {
      console.error('Chapters fetch error:', error);
      throw error;
    }
  }

  async getTopics(chapterId, subjectId, studentId) {
    try {
      const response = await authFetch(`${API_BASE}/topics/?chapter_id=${chapterId}&subject_id=${subjectId}&student_id=${studentId}`);
      if (!response.ok) throw new Error('Failed to fetch topics');
      return await response.json();
    } catch (error) {
      console.error('Topics fetch error:', error);
      throw error;
    }
  }

  async askTeachingDoubt(questionData) {
    try {
      const response = await authFetch(`${API_BASE}/teaching-doubt/ask?student_id=${questionData.student_id}`, {
        method: 'POST',
        
        body: JSON.stringify({
          question: questionData.question,
          session_id: questionData.session_id || null,
          student_id: questionData.student_id,
          subject_id: questionData.subject_id,
          chapter_id: questionData.chapter_id,
          topic_id: questionData.topic_id,
          subtopic_id: questionData.subtopic_id,
          chunk_id: questionData.chunk_id,
          micro_chunk_id: questionData.micro_chunk_id,
          micro_chunk_content: questionData.micro_chunk_content,
          conversation_history: questionData.conversation_history || [],
          language: questionData.language || 'en'
        })    
      });
      
      if (!response.ok) {
        throw new Error('Teaching doubt resolution failed');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Teaching doubt resolution error:', error);
      throw error;
    }
  }


  // Predefined QA Doubt Resolution
  async askPredefinedQADoubt(question, sessionId, studentId, qaId, subjectId, chapterId, topicId, subtopicId, language) {
    try {
      const response = await authFetch(`${API_BASE}/doubt-resolution/ask-predefined-qa?student_id=${studentId}`, {
        method: 'POST',
        
        body: JSON.stringify({
          question: question,
          session_id: sessionId,
          student_id: studentId,
          qa_id: qaId,
          subject_id: subjectId,
          chapter_id: chapterId,
          topic_id: topicId,
          subtopic_id: subtopicId,
          language: language
        })
      });
      
      if (!response.ok) {
        throw new Error('Predefined QA doubt resolution failed');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Predefined QA doubt error:', error);
      throw error;
    }
  }
  
  // Tree Editor API functions (add these to existing ApiService class)
  async getChapterTree(chapterId, subjectId) {
    try {
      const response = await authFetch(`${API_BASE}/admin/tree-editor/chapter-tree/${chapterId}?subject_id=${subjectId}`);
      if (!response.ok) throw new Error('Failed to fetch chapter tree');
      return await response.json();
    } catch (error) {
      console.error('Chapter tree fetch error:', error);
      throw error;
    }
  }
  async batchUpdateTree(changes, subjectId, chapterId) {
    try {
      const response = await authFetch(`${API_BASE}/admin/tree-editor/batch-update?subject_id=${subjectId}&chapter_id=${chapterId}`, {
        method: 'POST',
        
        body: JSON.stringify(changes)
      });
      
      if (!response.ok) throw new Error('Batch update failed');
      return await response.json();
    } catch (error) {
      console.error('Batch update error:', error);
      throw error;
    }
  }
  async getChapterSummary(chapterId, subjectId) {
    try {
      const response = await authFetch(`${API_BASE}/admin/tree-editor/chapter-summary/${chapterId}?subject_id=${subjectId}`);
      if (!response.ok) throw new Error('Failed to fetch chapter summary');
      return await response.json();
    } catch (error) {
      console.error('Chapter summary fetch error:', error);
      throw error;
    }
  }

  async getSubtopics(topicId, studentId) {
    try {
      const response = await authFetch(`${API_BASE}/subtopics/?topic_id=${topicId}&student_id=${studentId}`);
      if (!response.ok) throw new Error('Failed to fetch subtopics');
      return await response.json();
    } catch (error) {
      console.error('Subtopics fetch error:', error);
      throw error;
    }
  }

}        

const apiService = new ApiService();
export default apiService;
export { API_BASE, authFetch }; // Export API_BASE so other files can use it